import { Camera } from '../camera';
import { Context } from '../context/Context';
import { FullscreenQuad } from './FullscreenQuad';
import { GBuffer } from './GBuffer';
import { HistoryBuffer } from './HistoryBuffer';
import { RenderTexture } from './RenderTarget';

/**
 * Temporal anti-aliasing pass.
 *
 * The caller renders the scene into an HDR FBO and passes that texture in.
 * TaaPass blends it against the reprojected previous-frame history using
 * motion vectors from the G-buffer, writes the blend into the current history
 * slot, then composites the result back into the same HDR scene FBO so
 * downstream passes (bloom, FinalPresentPass, FXAA) see the smoothed image.
 *
 * Coexists with FXAA — FXAA can run after FinalPresentPass for residual
 * edge softening on the LDR image.
 */
export class TaaPass {
  private history!: HistoryBuffer;
  private width!: number;
  private height!: number;

  /** History weight; 0.9 = strong accumulation, 0.5 = light. */
  public blendAlpha = 0.1;

  init(ctx: Context): void {
    const gl = ctx.gl;
    this.width = ctx.viewPort.width;
    this.height = ctx.viewPort.height;
    this.history = new HistoryBuffer(gl, this.width, this.height);
  }

  invalidateHistory(): void {
    // Caller sets camera.historyValid = false; nothing else to do here.
  }

  render(
    ctx: Context,
    quad: FullscreenQuad,
    gBuffer: GBuffer,
    camera: Camera,
    inputScene: RenderTexture,
    outputBuffer: WebGLFramebuffer | null,
  ): void {
    const gl = ctx.gl;

    // === 1. TAA blend → write to current history slot ===
    ctx.textureState.clear();
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.history.currentFBO());
    gl.viewport(0, 0, this.width, this.height);
    gl.disable(gl.DEPTH_TEST);
    gl.disable(gl.BLEND);

    const taaShader = ctx.engine.shaderManager.getTaaShader();
    ctx.setShader(taaShader);

    taaShader.uniformsContainer
      .setCurrentColor(inputScene)
      .setHistoryColor(this.history.previous())
      .setMotionMap(gBuffer.motionTexture)
      .setTexelSize([1.0 / this.width, 1.0 / this.height])
      .setBlendAlpha(this.blendAlpha)
      .setHistoryValid(camera.historyValid ? 1 : 0);

    taaShader.validate();
    quad.draw(gl);

    // === 2. Passthrough current history → HDR scene FBO ===
    // Patches the scene FBO in place so bloom / FinalPresent / FXAA all see
    // the temporally smoothed image without needing to know TAA ran.
    ctx.textureState.clear();
    gl.bindFramebuffer(gl.FRAMEBUFFER, outputBuffer);
    gl.viewport(0, 0, ctx.viewPort.width, ctx.viewPort.height);

    const compositeShader = ctx.engine.shaderManager.getFullscreenCompositeShader();
    ctx.setShader(compositeShader);
    compositeShader.uniformsContainer.setTextureSampler(this.history.current());
    compositeShader.validate();
    quad.draw(gl);

    // === 3. Swap so this frame's result becomes next frame's "previous" ===
    this.history.swap();

    // After the first successful render the camera's history is valid; next
    // frame we'll blend instead of skipping.
    camera.historyValid = true;
  }

  destroy(gl: WebGL2RenderingContext): void {
    this.history.destroy(gl);
  }
}
